// Tabblad "Het corpus" van het vlak Bronnen & dekking (W6 ronde 3, research §3.4).
//
// Eén vraag: hoe ziet het corpus van deze sector eruit? Wie publiceert het,
// welke soort bron, welke taal, hoe vers, van de lijst of daarbuiten — en hoe
// verhouden de bronrollen zich tot hun doelen (regel C1, besluit D2).
//
// Alles komt kant-en-klaar van GET /api/beheer/bronnen: de tellingen, de
// aandelen, het stoplicht per bronrol en de adviezen als zinnen. Dit tabblad
// telt niets op en kleurt niets zelf. Waar de server geen aandeel stuurt (taal,
// inhoud, versheid) staat alleen de telling; de balk zelf is getekend tegen het
// totaal, maar er staat nergens een percentage dat wij verzonnen.

import type { ReactNode } from "react";

import { Card, TableHead } from "../../components/ui";
import { EmptyReason } from "../../components/Empty";
import type { BronnenResponse } from "../../contracts";
import { color, size, weight } from "../../tokens";
import { VinkIcoon } from "./Iconen";
import { Stoplicht } from "./Stoplicht";
import {
  Balken,
  Blok,
  Kpi,
  aantal,
  cijfer,
  datum,
  getal,
  noot,
  procent,
  type BalkRij,
} from "./BronnenStukken";

type Statistiek = BronnenResponse["statistiek"];

const TOP_KOLOMMEN = "minmax(170px,2fr) minmax(130px,1.4fr) 90px 84px 90px 110px 90px 110px";
const ROL_KOLOMMEN = "minmax(230px,2.4fr) 100px 90px 80px 70px";

/** Twee kolommen balkkaarten waar het past, één op een telefoon. */
const BALK_RASTER = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))",
  gap: 16,
} as const;

export function BronnenCorpus({ statistiek: s }: { statistiek: Statistiek }) {
  const u = s.uitgevers;
  const g = u.grenzen;
  const docs = s.documenten;

  // Het grootste jaar tekent de volle balk; de telling blijft het label.
  const jaarMax = Math.max(0, ...s.versheid.perJaar.map((j) => j.documenten));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 12,
        }}
      >
        <Kpi
          label="Documenten"
          waarde={getal(docs)}
          onder={`${getal(s.pdf.pdf)} pdf · ${getal(s.pdf.web)} webpagina's`}
        />
        <Kpi
          label="Uitgevers"
          waarde={getal(u.totaal)}
          // Documenten zonder uitgever horen bij geen enkele rij; zonder deze
          // regel vallen ze stil weg uit het beeld (de server telt ze apart).
          onder={
            `${getal(u.groot)} groot · ${getal(u.midden)} midden · ${getal(u.klein)} klein` +
            (u.zonderUitgever > 0
              ? ` · ${aantal(u.zonderUitgever, "document", "documenten")} zonder uitgever`
              : "")
          }
        />
        <Kpi
          label="Top 5 uitgevers"
          waarde={u.top5Aandeel !== null ? procent(u.top5Aandeel) : null}
          reden="geen aandeel: er zijn nog geen documenten"
          onder="van alle documenten komt van de vijf grootste uitgevers"
        />
      </div>

      <Blok titel="Uitgevers naar grootte">
        <Card style={{ padding: "14px 18px" }}>
          {/* De grenzen komen van de server (research §3.4); wij zetten ze in
              woorden. "tot" is exclusief: 50 zelf is groot. */}
          <Balken
            schaal={u.totaal}
            rijen={[
              { sleutel: "groot", label: `Groot · ${getal(g.groot)} of meer`, n: u.groot },
              {
                sleutel: "midden",
                label: `Midden · ${getal(g.midden)} tot ${getal(g.groot)}`,
                n: u.midden,
              },
              { sleutel: "klein", label: `Klein · minder dan ${getal(g.midden)}`, n: u.klein },
            ]}
          />
          <p style={noot}>
            Het getal is het aantal uitgevers; de grens telt hun documenten.
          </p>
        </Card>
      </Blok>

      <Blok titel="De grootste uitgevers">
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {u.top.length === 0 ? (
            <div style={{ padding: 18 }}>
              <EmptyReason reason="nog geen enkele uitgever in het corpus" />
            </div>
          ) : (
            // Op een smal scherm schuift de tabel binnen zijn kaart, niet de pagina.
            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 820 }}>
                <TableHead
                  cols={TOP_KOLOMMEN}
                  labels={[
                    "Uitgever",
                    "Bronsoort",
                    "Documenten",
                    "Door de lat",
                    "Weggelaten",
                    "Trendmateriaal",
                    "Op de lijst",
                    "Nieuwste",
                  ]}
                />
                {u.top.map((t, i) => (
                  <div
                    key={t.domein}
                    data-uitgever={t.domein}
                    style={{
                      display: "grid",
                      gridTemplateColumns: TOP_KOLOMMEN,
                      alignItems: "center",
                      padding: "10px 16px",
                      borderTop: i === 0 ? "none" : `1px solid ${color.lineFaint}`,
                      fontSize: size.meta,
                      color: color.body,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: weight.medium,
                        color: color.ink,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {t.domein}
                    </span>
                    <span>{t.bronsoort ?? <EmptyReason reason="niet getypeerd" />}</span>
                    <span style={cijfer}>{getal(t.documenten)}</span>
                    <span style={cijfer}>{getal(t.door)}</span>
                    <span style={cijfer}>{getal(t.weggelaten)}</span>
                    <span style={cijfer}>{getal(t.trendmateriaal)}</span>
                    <span style={{ textAlign: "center" }}>
                      {t.opDeLijst ? (
                        <span
                          role="img"
                          aria-label="staat op de lijst"
                          title="Staat op de bronnenlijst van deze sector"
                          style={{ display: "inline-flex", color: color.accentGreen }}
                        >
                          <VinkIcoon />
                        </span>
                      ) : (
                        <span style={{ color: color.faint }}>nee</span>
                      )}
                    </span>
                    <span style={{ textAlign: "center" }}>
                      {datum(t.nieuwste) ?? <EmptyReason reason="geen datum" />}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </Blok>

      <div style={BALK_RASTER}>
        <BalkKaart titel="Bronsoort">
          {s.bronsoort.length === 0 ? (
            <EmptyReason reason="nog geen documenten om naar bronsoort te tellen" />
          ) : (
            <Balken
              schaal={docs}
              rijen={s.bronsoort.map(
                (b): BalkRij => ({
                  sleutel: b.sleutel,
                  label: b.label,
                  n: b.documenten,
                  aandeel: b.aandeel,
                  title: `${getal(b.door)} door de lat · ${aantal(b.uitgevers, "uitgever", "uitgevers")}`,
                }),
              )}
            />
          )}
        </BalkKaart>

        <BalkKaart titel="Taal">
          <Balken
            schaal={docs}
            rijen={[
              { sleutel: "nl", label: "Nederlands", n: s.taal.nl },
              { sleutel: "en", label: "Engels", n: s.taal.en },
              { sleutel: "anders", label: "Andere taal", n: s.taal.anders },
              { sleutel: "leeg", label: "Niet vastgesteld", n: s.taal.leeg },
            ]}
          />
          {s.taal.reden && <p style={noot}>{s.taal.reden}</p>}
        </BalkKaart>

        <BalkKaart titel="Soort inhoud">
          <Balken
            schaal={docs}
            rijen={[
              { sleutel: "vakinhoudelijk", label: "Vakinhoudelijk", n: s.inhoud.vakinhoudelijk },
              { sleutel: "gemengd", label: "Gemengd", n: s.inhoud.gemengd },
              { sleutel: "achtergrond", label: "Achtergrond", n: s.inhoud.achtergrond },
              // Alleen tonen als er iets onbekends is: een rij "onbekend 0"
              // voegt niets toe aan drie rijen die samen het corpus zijn.
              ...(s.inhoud.onbekend > 0
                ? [{ sleutel: "onbekend", label: "Onbekend", n: s.inhoud.onbekend }]
                : []),
            ]}
          />
          <div style={{ borderTop: `1px solid ${color.lineFaint}`, margin: "12px 0 10px" }} />
          <Balken
            schaal={docs}
            rijen={[
              {
                sleutel: "metArbeidsmarkt",
                label: "Met arbeidsmarkt­waarnemingen",
                n: s.inhoud.metArbeidsmarkt,
              },
            ]}
          />
          <p style={noot}>Telt los: een document kan hierboven én hier staan.</p>
        </BalkKaart>

        <BalkKaart titel="Versheid">
          <Balken
            schaal={docs}
            rijen={[
              { sleutel: "recent", label: "Recent", n: s.versheid.recent },
              { sleutel: "ouder", label: "Ouder", n: s.versheid.ouder },
              { sleutel: "toekomst", label: "Datum in de toekomst", n: s.versheid.toekomst },
              { sleutel: "zonderDatum", label: "Zonder datum", n: s.versheid.zonderDatum },
            ]}
          />
          {/* Wat "recent" betekent zegt de server, met de grens erin — het scherm
              kent geen eigen termijn. */}
          <p style={noot}>{s.versheid.uitleg}</p>
          {s.versheid.perJaar.length > 0 && (
            <>
              <div style={{ fontSize: 12, color: color.faint, margin: "14px 0 8px" }}>
                Per jaar van publicatie
              </div>
              <Balken
                schaal={jaarMax}
                rijen={s.versheid.perJaar.map((j) => ({
                  sleutel: `jaar-${j.jaar}`,
                  label: String(j.jaar),
                  n: j.documenten,
                }))}
              />
            </>
          )}
        </BalkKaart>

        <BalkKaart titel="Van de lijst of daarbuiten">
          <Balken
            schaal={docs}
            rijen={[
              { sleutel: "vanDeLijst", label: "Van de bronnenlijst", n: s.lijst.vanDeLijst },
              { sleutel: "buitenDeLijst", label: "Van buiten de lijst", n: s.lijst.buitenDeLijst },
            ]}
          />
        </BalkKaart>
      </div>

      <Blok titel="Bronrollen tegen de doelen">
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {s.bronrollen.length === 0 ? (
            <div style={{ padding: 18 }}>
              <EmptyReason reason="de server gaf geen bronrollen terug" />
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 580 }}>
                <TableHead
                  cols={ROL_KOLOMMEN}
                  labels={["Bronrol", "Documenten", "Aandeel", "Doel", "Stand"]}
                />
                {s.bronrollen.map((r, i) => {
                  const soorten = bronsoortWoorden(r.typen, s.bronsoort);
                  return (
                    <div
                      key={r.sleutel}
                      data-bronrol={r.sleutel}
                      style={{
                        display: "grid",
                        gridTemplateColumns: ROL_KOLOMMEN,
                        alignItems: "center",
                        padding: "10px 16px",
                        borderTop: i === 0 ? "none" : `1px solid ${color.lineFaint}`,
                        fontSize: size.meta,
                        color: color.body,
                      }}
                    >
                      <span
                        title={soorten ? `Via de bronsoorten: ${soorten}` : undefined}
                        style={{ color: color.ink, fontWeight: weight.medium }}
                      >
                        {r.label}
                      </span>
                      <span style={cijfer}>{getal(r.documenten)}</span>
                      <span style={cijfer}>
                        {r.aandeel !== null ? (
                          procent(r.aandeel)
                        ) : (
                          <EmptyReason reason={r.reden ? "zie hieronder" : "geen aandeel"} />
                        )}
                      </span>
                      <span style={{ ...cijfer, textAlign: "center" }}>
                        {r.doel !== null ? (
                          procent(r.doel)
                        ) : (
                          <EmptyReason reason={r.reden ? "zie hieronder" : "geen doel"} />
                        )}
                      </span>
                      <span style={{ textAlign: "center" }}>
                        <Stoplicht stand={r.stand} reden={r.reden} />
                      </span>
                      {/* Waarom een vak leeg is zegt de server; die zin wint van
                          ons eigen "geen doel". Op de hele breedte, want in een
                          smalle kolom wordt een zin een toren. */}
                      {r.reden && (
                        <p
                          data-reden
                          style={{
                            gridColumn: "1 / -1",
                            marginTop: 6,
                            fontSize: 12,
                            color: color.faint,
                            lineHeight: 1.5,
                          }}
                        >
                          {r.reden}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>

        {/* De adviezen zijn zinnen van de server (vaste regels, research §3.4);
            wij zetten ze onder elkaar, met het stoplicht dat hij erbij gaf. */}
        <Card style={{ padding: "12px 16px", marginTop: 12 }}>
          {s.adviezen.length === 0 ? (
            <EmptyReason reason="geen adviezen voor deze sector" />
          ) : (
            <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
              {s.adviezen.map((a, i) => (
                <li
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 10,
                    fontSize: size.body,
                    color: color.body,
                    lineHeight: 1.55,
                  }}
                >
                  <Stoplicht stand={a.stand} />
                  <span>{a.tekst}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <p style={noot}>
          De koppeling van uitgeverstype naar bronrol is een concept — de owner stelt haar vast.
        </p>
      </Blok>
    </div>
  );
}

/**
 * De bronsoorten achter een bronrol, in de woorden die de server er in dit
 * antwoord bij gaf. Een sleutel die hij niet benoemde tonen we niet kaal: dan
 * liever geen tooltip dan een halve of een met codes. Het label van de bronrol
 * zegt het al in woorden.
 */
function bronsoortWoorden(
  typen: readonly string[],
  bronsoort: Statistiek["bronsoort"],
): string | null {
  const woorden = typen.map((t) => bronsoort.find((b) => b.sleutel === t)?.label);
  if (!woorden.length || woorden.some((w) => !w)) return null;
  return woorden.join(", ");
}

function BalkKaart({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <Blok titel={titel}>
      <Card style={{ padding: "14px 18px" }}>{children}</Card>
    </Blok>
  );
}
